import { ToolExecutionState } from 'ducks/chatDrawer'
import { CheckOutlineIcon, keyframes, styled } from 'opensolar-ui'
import { useTranslate } from 'ra-core'
import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useChatScroll } from '../../ChatScrollContext'
import { loadInverterDesignAssistantResult } from '../../utils/loadInverterDesignAssistantResult'
import { BaseToolProgressItem } from '../shared/BaseToolProgressItem'
import { InverterRecommendationsList } from './InverterRecommendationsList'

interface InverterDesignAssistantProgressItemProps {
  toolExecutionState: ToolExecutionState
  messageIndex: number
}

const fadeIn = keyframes`
  0% {
    opacity: 0;
    transform: translateY(-4px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`

const Wrapper = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  animation: `${fadeIn} 0.3s ease-out`,
})

export const InverterDesignAssistantProgressItem: React.FC<InverterDesignAssistantProgressItemProps> = ({
  toolExecutionState,
  messageIndex,
}) => {
  const dispatch = useDispatch()
  const translate = useTranslate()
  const { scrollToBottom } = useChatScroll()
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null)
  const inverterSets = toolExecutionState.data?.inverterSets || []

  useEffect(() => {
    if (toolExecutionState.status === 'ready' && inverterSets.length > 0) {
      setTimeout(() => {
        scrollToBottom()
      }, 100)
    }
  }, [toolExecutionState.status, inverterSets.length, scrollToBottom])

  const handleLoad = async () => {
    if (selectedIndex === null) {
      return
    }

    try {
      await loadInverterDesignAssistantResult(selectedIndex)

      // Update status to completed
      dispatch({
        type: 'UPDATE_TOOL_EXECUTION_STATE',
        messageIndex,
        toolExecutionState: {
          ...toolExecutionState,
          status: 'completed',
          description: translate('Inverters Added', { _: 'Inverters Added' }),
        },
      })
    } catch (error: any) {
      console.error('Error loading inverter design assistant result:', error)
      // Update status to error
      dispatch({
        type: 'UPDATE_TOOL_EXECUTION_STATE',
        messageIndex,
        toolExecutionState: {
          ...toolExecutionState,
          status: 'error',
          error: {
            message: error?.message || 'Failed to load inverter recommendations',
          },
        },
      })
    }
  }

  // If we have recommendations in ready state, show custom UI with recommendations
  if (toolExecutionState.status === 'ready' && inverterSets.length > 0) {
    return (
      <Wrapper>
        <BaseToolProgressItem
          toolExecutionState={toolExecutionState}
          messageIndex={messageIndex}
          runningLabel="Generating inverter suggestions..."
          completedLabel="Inverter recommendations loaded"
          errorLabel="Inverter design assistant failed"
          readyLabel="Ready"
          showCountdown={{ completed: 5, error: 15 }}
          onReadyAction={handleLoad}
          readyActionLabel="Load Inverters"
          readyActionIcon={<CheckOutlineIcon size={16} color="#6B7280" />}
          readyActionDisabled={selectedIndex === null}
          readyActionTooltip={
            selectedIndex === null
              ? 'Please select an inverter option first'
              : 'Load selected inverters into the system'
          }
        />
        <InverterRecommendationsList
          inverterSets={inverterSets}
          selectedIndex={selectedIndex}
          onSelect={setSelectedIndex}
        />
      </Wrapper>
    )
  }

  // Otherwise use the base component
  return (
    <BaseToolProgressItem
      toolExecutionState={toolExecutionState}
      messageIndex={messageIndex}
      runningLabel="Generating inverter suggestions..."
      completedLabel="Inverter recommendations loaded"
      errorLabel="Inverter design assistant failed"
      readyLabel="Ready"
      showCountdown={{ completed: 5, error: 15 }}
    />
  )
}
